/**
 * ManaSymbol - Renders MTG mana symbols using mana-font
 *
 * Converts Scryfall-style mana notation {W}, {U}, {B}, {R}, {G}, {C}, {T}, {X}, {1}, {2/W}, etc.
 * to proper mana-font icon classes.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

export interface ManaSymbolProps {
  /** Mana symbol code, e.g. "W", "U", "2", "T", "W/U", "2/W", "W/P" */
  symbol: string;
  /** Show as casting cost (with circle background) */
  cost?: boolean;
  /** Add drop shadow */
  shadow?: boolean;
  /** Size multiplier (2x, 3x, etc.) */
  size?: '2x' | '3x' | '4x' | '5x' | '6x';
  /** Additional class names */
  className?: string;
}

/**
 * Map Scryfall symbol notation to mana-font class names
 */
function getSymbolClass(symbol: string): string {
  const normalized = symbol.toLowerCase().replace(/\//g, '');

  // Handle common mappings
  const mappings: Record<string, string> = {
    // Basic mana
    w: 'w',
    u: 'u',
    b: 'b',
    r: 'r',
    g: 'g',
    c: 'c',
    // Generic/colorless numbers (0-20)
    '0': '0',
    '1': '1',
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
    '6': '6',
    '7': '7',
    '8': '8',
    '9': '9',
    '10': '10',
    '11': '11',
    '12': '12',
    '13': '13',
    '14': '14',
    '15': '15',
    '16': '16',
    '17': '17',
    '18': '18',
    '19': '19',
    '20': '20',
    // Special symbols
    x: 'x',
    y: 'y',
    z: 'z',
    t: 'tap',
    tap: 'tap',
    q: 'untap',
    untap: 'untap',
    s: 's', // Snow
    e: 'e', // Energy
    // Phyrexian mana
    wp: 'wp',
    up: 'up',
    bp: 'bp',
    rp: 'rp',
    gp: 'gp',
    p: 'p', // Generic Phyrexian
    pw: 'wp',
    pu: 'up',
    pb: 'bp',
    pr: 'rp',
    pg: 'gp',
    // Hybrid mana (color pairs)
    wu: 'wu',
    uw: 'wu',
    wb: 'wb',
    bw: 'wb',
    ub: 'ub',
    bu: 'ub',
    ur: 'ur',
    ru: 'ur',
    br: 'br',
    rb: 'br',
    bg: 'bg',
    gb: 'bg',
    rg: 'rg',
    gr: 'rg',
    rw: 'rw',
    wr: 'rw',
    gw: 'gw',
    wg: 'gw',
    gu: 'gu',
    ug: 'gu',
    // Two-brid (2/color) hybrid
    '2w': '2w',
    '2u': '2u',
    '2b': '2b',
    '2r': '2r',
    '2g': '2g',
    // Phyrexian hybrid
    wup: 'wup',
    wbp: 'wbp',
    ubp: 'ubp',
    urp: 'urp',
    brp: 'brp',
    bgp: 'bgp',
    rgp: 'rgp',
    rwp: 'rwp',
    gwp: 'gwp',
    gup: 'gup',
    // Misc
    chaos: 'chaos',
    infinity: 'infinity',
  };

  return mappings[normalized] || normalized;
}

/**
 * The mana-font `ms-cost` CSS sets `height: 1.3em` and `font-size: 0.95em`,
 * so each cost pill renders at 1.3 × 0.95 = 1.235× the inherited font-size.
 * Use this factor to convert a desired visible pill height to the CSS font-size
 * needed on the wrapper element.
 */
export const MS_COST_SIZE_FACTOR = 1.3 * 0.95;

/** Convert a desired icon-pill height (px) to the CSS font-size needed for `ms-cost` icons. */
export function costIconFontSize(desiredHeight: number): number {
  return desiredHeight / MS_COST_SIZE_FACTOR;
}

export default function ManaSymbol({ symbol, cost = false, shadow = false, size, className }: ManaSymbolProps): ReactElement {
  const symbolClass = getSymbolClass(symbol);

  return (
    <i
      className={clsx('ms', `ms-${symbolClass}`, cost && 'ms-cost', shadow && 'ms-shadow', size && `ms-${size}`, className)}
      aria-label={`{${symbol.toUpperCase()}}`}
    />
  );
}

/**
 * Parse a mana cost string like "{2}{W}{U}" and return array of symbol codes
 */
export function parseManaCost(manaCost: string): string[] {
  const matches = manaCost.match(/\{([^}]+)\}/g);
  if (!matches) return [];
  return matches.map((m) => m.slice(1, -1));
}

/**
 * Render a full mana cost string as ManaSymbol components
 */
export function ManaCost({
  cost,
  shadow = true,
  size,
  className,
  /** Desired visible height of each icon pill in px (adjusts font-size for ms-cost sizing) */
  iconSize,
}: {
  cost: string;
  shadow?: boolean;
  size?: ManaSymbolProps['size'];
  className?: string;
  iconSize?: number;
}): ReactElement {
  const symbols = parseManaCost(cost);

  return (
    <span
      className={clsx('inline-flex items-center gap-0.5', className)}
      style={iconSize ? { fontSize: costIconFontSize(iconSize) } : undefined}
    >
      {symbols.map((symbol, i) => (
        <ManaSymbol key={i} symbol={symbol} cost shadow={shadow} size={size} />
      ))}
    </span>
  );
}

/**
 * Render text with inline mana symbols (for oracle text)
 * Replaces {X} patterns with ManaSymbol components
 */
export function TextWithMana({ text, className }: { text: string; className?: string }): ReactElement {
  const parts = text.split(/(\{[^}]+\})/g);

  return (
    <span className={className}>
      {parts.map((part, i) => {
        const match = part.match(/^\{([^}]+)\}$/);
        if (match) {
          return <ManaSymbol key={i} symbol={match[1]} cost shadow />;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}
